import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Row, Col, Label, Input, Button } from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { handleAuthError } from "../../utils";

const teacherName = (t) => `${t?.firstName || ""} ${t?.lastName || ""}`.trim() || `Teacher ${t?.id}`;
const studentName = (student) => `${student?.firstName || ""} ${student?.lastName || ""}`.trim() || `Student ${student?.childId}`;
const teacherNames = (student) => (student?.teachers || []).map((teacher) => teacher.name).filter(Boolean).join(", ");

/**
 * Per-student checklist totals for one cohort. Legacy attendance is a different report.
 */
const AttendanceMatrix = ({ reportUrl, cohorts = [], teachers = [] }) => {
  const sortedCohorts = useMemo(
    () => [...cohorts].sort((a, b) => Number(!!b.isStarted) - Number(!!a.isStarted) || (b.id || 0) - (a.id || 0)),
    [cohorts]
  );
  const [cohortId, setCohortId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cohortId) {
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (teacherId) params.teacherId = teacherId;
        if (from) params.from = from;
        if (to) params.to = to;
        const response = await axios.get(`${reportUrl}/${cohortId}`, { params });
        if (cancelled) return;
        if (response.data?.status) setReport(response.data.data);
        else {
          setReport(null);
          setError(response.data?.message || "Could not load attendance");
        }
      } catch (err) {
        handleAuthError(err);
        if (!cancelled) {
          setReport(null);
          setError(err.response?.data?.message || "Could not load attendance");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reportUrl, cohortId, teacherId, from, to]);

  const students = useMemo(() => (Array.isArray(report?.students) ? report.students : []), [report]);
  const visibleStudents = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return students;
    return students.filter((student) => `${studentName(student)} ${teacherNames(student)}`.toLowerCase().includes(needle));
  }, [students, query]);
  const totals = useMemo(() => students.reduce((sum, student) => ({
    present: sum.present + (student.present || 0),
    absent: sum.absent + (student.absent || 0),
    rescheduledPresent: sum.rescheduledPresent + (student.rescheduledPresent || 0),
    rescheduledAbsent: sum.rescheduledAbsent + (student.rescheduledAbsent || 0),
  }), { present: 0, absent: 0, rescheduledPresent: 0, rescheduledAbsent: 0 }), [students]);

  const exportXlsx = () => {
    const data = students.map((student) => ({
      Student: studentName(student),
      Teacher: teacherNames(student),
      Present: student.present || 0,
      Absent: student.absent || 0,
      "Rescheduled Present": student.rescheduledPresent || 0,
      "Rescheduled Absent": student.rescheduledAbsent || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = { Sheets: { attendance: ws }, SheetNames: ["attendance"] };
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const title = (report?.cohort?.title || `cohort_${cohortId}`).replace(/[^\w-]+/g, "_");
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), `attendance_${title}.xlsx`);
  };

  return (
    <div>
      <h5 className="mb-1">Cohort attendance</h5>
      <p className="text-muted small">
        Each paid student registered in the selected cohort, with that student&apos;s own totals.
        Present and Absent are the original class. Rescheduled Present and Rescheduled Absent are the moved class, counted once the new date is marked.
        An unmarked class is not counted as absent. Leave the dates empty to include every saved class for this cohort.
      </p>
      <Row className="g-3 align-items-end mb-3">
        <Col md={3}>
          <Label className="form-label mb-1">Cohort</Label>
          <Input type="select" value={cohortId} onChange={(e) => { setCohortId(e.target.value); setQuery(""); }}>
            <option value="">Select a cohort</option>
            {sortedCohorts.map((c) => (
              <option key={c.id} value={c.id}>{c.title}{c.isStarted ? " (ongoing)" : ""}</option>
            ))}
          </Input>
        </Col>
        <Col md={3}>
          <Label className="form-label mb-1">Teacher</Label>
          <Input type="select" value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
            <option value="">All teachers</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>{teacherName(t)}{t.status === false ? " (Inactive)" : ""}</option>
            ))}
          </Input>
        </Col>
        <Col md={2}>
          <Label className="form-label mb-1">From (WAT)</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Col>
        <Col md={2}>
          <Label className="form-label mb-1">To (WAT)</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Col>
        <Col md={2} className="text-md-end">
          <Button color="success" outline disabled={!students.length} onClick={exportXlsx}>
            <i className="mdi mdi-file-excel me-1" /> Export
          </Button>
        </Col>
      </Row>

      {!cohortId ? (
        <div className="text-muted py-3">Select a cohort to see each student&apos;s attendance.</div>
      ) : loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <Row className="mb-2">
            <Col md={4}>
              <Input
                type="search"
                value={query}
                placeholder="Search student or teacher"
                onChange={(e) => setQuery(e.target.value)}
              />
            </Col>
          </Row>
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Student</th>
                  <th>Teacher</th>
                  <th className="text-center">Present</th>
                  <th className="text-center">Absent</th>
                  <th className="text-center">Rescheduled Present</th>
                  <th className="text-center">Rescheduled Absent</th>
                </tr>
              </thead>
              <tbody>
                {visibleStudents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted text-center py-3">
                      {students.length === 0 ? "No paid students are registered in this cohort." : "No students match that search."}
                    </td>
                  </tr>
                ) : visibleStudents.map((student) => (
                  <tr key={student.childId}>
                    <td>{studentName(student)}</td>
                    <td>{teacherNames(student) || "—"}</td>
                    <td className="text-center">{student.present || 0}</td>
                    <td className="text-center">{student.absent || 0}</td>
                    <td className="text-center">{student.rescheduledPresent || 0}</td>
                    <td className="text-center">{student.rescheduledAbsent || 0}</td>
                  </tr>
                ))}
              </tbody>
              {students.length > 0 && (
                <tfoot>
                  <tr className="table-light">
                    <th colSpan={2}>Cohort total ({students.length} students)</th>
                    <th className="text-center">{totals.present}</th>
                    <th className="text-center">{totals.absent}</th>
                    <th className="text-center">{totals.rescheduledPresent}</th>
                    <th className="text-center">{totals.rescheduledAbsent}</th>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AttendanceMatrix;
